import { ref, computed, inject } from 'vue'
import { 
  useAIChat, formatHistoryToMessages, extractPersonalInfo
} from './ai-api.js'
import { useWebSocket, formatNotification } from './websocket.js'

// Global state for chat
const chatState = ref({
  isConnected: false,
  agentStatus: 'Online',
  sessionId: null,
  messages: [],
  unreadCount: 0,
  isAIMode: true, // Flag to indicate if using AI API
  wsNotifications: [], // WebSocket notifications
  wsConnected: false, // WebSocket connection status
  isReloading: false, // Prevent multiple simultaneous reloads
  lastReloadTime: 0, // Track last reload time for debouncing
  isLoadingSmoothly: false, // Indicate smooth loading in progress
  pendingMessages: 0 // Track pending messages being added
})

// Track chat panel open/close state (separate from WebSocket)
const isChatPanelOpen = ref(false)

export const useLiveChatStore = () => {
  // Get hostname from configs
  const configs = inject('configs', null)
  const hostname = configs?.hostname || 'localhost'
  
  // AI Chat integration with hostname
  const { 
    sessionId: aiSessionId,
    conversationHistory,
    conversationMetadata,
    isLoadingHistory,
    isLoadingResponse,
    lastResponseMetadata,
    sendMessageToAI,
    loadConversationHistory,
    initializeSession: initializeAISession,
    clearSession: clearAISession,
    getLastResponseMetadata,
    createSessionId, // เพิ่มฟังก์ชันสำหรับสร้าง session
    updatePersonalInfo, // เพิ่มฟังก์ชันสำหรับอัพเดทข้อมูลส่วนบุคคล
    getPersonalInfoStatus, // เพิ่มฟังก์ชันสำหรับตรวจสอบสถานะข้อมูล
    personalInfo // เพิ่ม state ข้อมูลส่วนบุคคล
  } = useAIChat(hostname)

  // WebSocket integration
  const {
    isConnected: wsIsConnected,
    isConnecting: wsIsConnecting,
    connectionId: wsConnectionId,
    notifications: wsNotifications,
    connect: wsConnect,
    disconnect: wsDisconnect,
    addEventListener: wsAddEventListener,
    removeEventListener: wsRemoveEventListener,
    getConnectionStats: wsGetConnectionStats,
    clearNotifications: wsClearNotifications
  } = useWebSocket(hostname)

  // WebSocket event handlers setup
  const setupWebSocketHandlers = () => {
    console.log('[LiveChat] 🔧 Setting up WebSocket event handlers...')
    
    // Handle new message notifications - trigger reload AND increment unread count
    wsAddEventListener('new_message', async (data) => {
      console.log('[LiveChat] 💬 WebSocket: New message trigger received!', data)
      console.log('[LiveChat] 📊 Current state:', {
        currentSessionId: chatState.value.sessionId,
        incomingSessionId: data.session_id,
        isChatPanelOpen: isChatPanelOpen.value,
        currentUnreadCount: chatState.value.unreadCount,
        isReloading: chatState.value.isReloading
      })
      
      // Always increment unread count for new messages (even when chat is closed)
      if (data.session_id === chatState.value.sessionId) {
        chatState.value.unreadCount++
        console.log('[LiveChat] 📈 Incremented unread count:', chatState.value.unreadCount)
      } else {
        console.log('[LiveChat] ⚠️ New message for different session:', data.session_id, 'vs current:', chatState.value.sessionId)
      }
      
      // If it's for current session and not already reloading AND chat is open
      if (data.session_id === chatState.value.sessionId && !chatState.value.isReloading && isChatPanelOpen.value) {
        // Debounce: only reload if last reload was more than 2 seconds ago
        const now = Date.now()
        if (now - chatState.value.lastReloadTime > 2000) {
          console.log('[LiveChat] 🔄 Reloading conversation for current session (chat is open)')
          await reloadConversation()
        } else {
          console.log('[LiveChat] ⏳ Reload debounced - too soon after last reload')
        }
      } else if (data.session_id === chatState.value.sessionId) {
        console.log('[LiveChat] 💤 New message received but chat is closed - only updated unread count')
      }
    })

    // Handle new session notifications - trigger reload with debounce
    wsAddEventListener('new_session', async (data) => {
      console.log('[LiveChat] 🆕 WebSocket: New session trigger received!', data)
      
      // Only reload if not already reloading and debounce check AND chat is open
      const now = Date.now()
      if (!chatState.value.isReloading && now - chatState.value.lastReloadTime > 2000 && isChatPanelOpen.value) {
        console.log('[LiveChat] 🔄 Reloading conversation for new session (chat is open)')
        await reloadConversation()
      } else {
        console.log('[LiveChat] 💤 New session detected but chat is closed or debounced')
      }
    })

    // Handle status change notifications - DISABLED for testing
    // wsAddEventListener('status_change', (data) => {
    //   console.log('[LiveChat] WebSocket: Status change trigger', data)
    //   
    //   // Update agent status if applicable
    //   if (data.data?.status) {
    //     chatState.value.agentStatus = data.data.status
    //     console.log('[LiveChat] Agent status updated to:', data.data.status)
    //   }
    // })

    // Handle connection events
    wsAddEventListener('connection_open', () => {
      console.log('[LiveChat] 🟢 WebSocket connected successfully!')
      chatState.value.wsConnected = true
    })

    wsAddEventListener('connection_close', () => {
      console.log('[LiveChat] 🔴 WebSocket disconnected')
      chatState.value.wsConnected = false
    })

    wsAddEventListener('connection_error', (data) => {
      console.error('[LiveChat] ❌ WebSocket error:', data.error)
      chatState.value.wsConnected = false
    })
    
    console.log('[LiveChat] ✅ Event handlers registered: new_message, new_session, connection_*')
  }

  // Initialize WebSocket (independent of chat panel state)
  const initializeWebSocket = async () => {
    console.log('[LiveChat] 🚀 Initializing WebSocket...')
    
    // Setup WebSocket handlers
    setupWebSocketHandlers()
    console.log('[LiveChat] ✅ WebSocket handlers setup complete')
    
    // Connect to WebSocket
    try {
      console.log('[LiveChat] 🔌 Attempting WebSocket connection...')
      await wsConnect()
      console.log('[LiveChat] ✅ WebSocket connection established successfully!')
    } catch (error) {
      console.error('[LiveChat] ❌ Failed to connect WebSocket:', error)
    }
  }

  // Initialize session with AI และโหลดประวัติใหม่ทุกครั้ง
  const initializeChat = async () => {
    console.log('[LiveChat] 🔄 initializeChat called - refreshing session and history...')
    
    // Initialize WebSocket if not already connected
    if (!wsIsConnected.value && !wsIsConnecting.value) {
      console.log('[LiveChat] 🔌 WebSocket not connected, initializing...')
      await initializeWebSocket()
    }
    
    // ตรวจสอบ session ที่มีอยู่ แต่ไม่สร้างใหม่
    const existingSession = await initializeAISession() // จะไม่สร้าง session ใหม่
    
    if (existingSession) {
      chatState.value.sessionId = existingSession
      chatState.value.isConnected = true
      
      console.log('[LiveChat] 📝 Found existing session:', existingSession)
      console.log('[LiveChat] 🎯 WebSocket will listen for session:', existingSession)
      
      // โหลดประวัติการสนทนาใหม่ทุกครั้ง (ไม่ cache)
      console.log('[LiveChat] 📚 Loading fresh conversation history...')
      const history = await loadConversationHistory()
      
      if (history && history.length > 0) {
        const formattedMessages = formatHistoryToMessages(history)
        chatState.value.messages = formattedMessages
        console.log('[LiveChat] ✅ Loaded', formattedMessages.length, 'messages from history')
      } else {
        console.log('[LiveChat] 📭 No conversation history found')
        chatState.value.messages = []
      }
      
      console.log('[LiveChat] ✅ Chat session refreshed:', chatState.value.sessionId)
      console.log('[LiveChat] 📊 Current messages count:', chatState.value.messages.length)
    } else {
      console.log('[LiveChat] ❌ No existing session found - will create when user interacts')
      console.log('[LiveChat] ⚠️ WebSocket will not receive notifications until session is created')
      // Clear messages when no session
      chatState.value.messages = []
    }
  }

  // Open chat panel
  const openChatPanel = async () => {
    isChatPanelOpen.value = true
    
    // Initialize chat when panel opens
    if (!chatState.value.isConnected) {
      await initializeChat()
    }
    
    // Mark messages as read when opening panel
    markAsRead()
  }

  // Close chat panel (but keep WebSocket alive)
  const closeChatPanel = () => {
    isChatPanelOpen.value = false
    // NOTE: WebSocket stays connected to receive notifications
  }

  // Add message to chat
  const addMessage = (type, text, metadata = {}) => {
    const message = {
      id: metadata.id || `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type, // 'user' or 'agent'
      text,
      timestamp: new Date(),
      metadata,
      isHistory: false,
      isFromHistory: false,
      isLoading: metadata.isLoading || false // สำหรับ loading messages
    }
    
    chatState.value.messages.push(message)
    
    // Update unread count for agent messages (but not for loading messages)
    if (type === 'agent' && !metadata.isLoading) {
      chatState.value.unreadCount++
    }
    
    return message
  }

  // Remove message by ID
  const removeMessage = (messageId) => {
    const index = chatState.value.messages.findIndex(msg => msg.id === messageId)
    if (index !== -1) {
      chatState.value.messages.splice(index, 1)
      console.log('[Chat] Removed message:', messageId)
    }
  }

  // Send message and get AI response
  const sendUserMessage = async (userMessage) => {
    // สร้าง session ถ้าไม่มี (เมื่อผู้ใช้ส่งข้อความ)
    if (!chatState.value.sessionId) {
      console.log('[LiveChat] 🆕 Creating new session for first message...')
      const newSession = await createSessionId() // สร้าง session ใหม่
      chatState.value.sessionId = newSession
      chatState.value.isConnected = true
      console.log('[LiveChat] ✅ New chat session created:', newSession)
      console.log('[LiveChat] 🎯 WebSocket now listening for session:', newSession)
    }
    
    // Extract personal information from user message
    const extractedInfo = extractPersonalInfo(userMessage)
    if (Object.keys(extractedInfo).length > 0) {
      console.log('Extracted personal info:', extractedInfo)
      // Update personal information
      Object.entries(extractedInfo).forEach(([field, value]) => {
        if (value) updatePersonalInfo(field, value)
      })
    }
    
    // Add user message to chat
    const userMsg = addMessage('user', userMessage)
    
    // Check conversation control status to determine if we should show loading bubble
    const shouldShowLoadingBubble = () => {
      // Check if conversation metadata has control_status
      if (conversationMetadata.value && conversationMetadata.value.control_status) {
        const mode = conversationMetadata.value.control_status.mode
        console.log(`[Chat] Control mode: ${mode}`)
        // Only show loading bubble if mode is "ai"
        return mode === 'ai'
      }
      // Default to showing loading bubble if no control_status found
      console.log('[Chat] No control_status found, defaulting to AI mode')
      return true
    }
    
    let loadingMessage = null
    
    // Add loading bubble message only if in AI mode
    if (shouldShowLoadingBubble()) {
      loadingMessage = addMessage('agent', 'AI กำลังประมวลผล...', {
        isLoading: true,
        id: `loading-${Date.now()}`
      })
      console.log('[Chat] Added loading bubble for AI mode')
    } else {
      console.log('[Chat] Skipped loading bubble for admin mode')
    }
    
    try {
      // Send to AI API (don't display response immediately)
      const aiResponse = await sendMessageToAI(userMessage)
      
      console.log('[Chat] Message sent to AI, reloading conversation to get response from history')
      
      // Remove loading message if it was created
      if (loadingMessage) {
        removeMessage(loadingMessage.id)
      }
      
      // Reload conversation to get the AI response from history
      // This ensures we display exactly what's in the database
      setTimeout(async () => {
        await reloadConversation()
      }, 1000) // Small delay to allow AI to process and save response
      
      // Don't add AI response to chat - let history reload handle it
      return null
    } catch (error) {
      console.error('Error sending message to AI:', error)
      
      // Remove loading message on error (if it exists)
      if (loadingMessage) {
        removeMessage(loadingMessage.id)
      }
      
      // Add error message only if AI request failed
      const errorMsg = addMessage('agent', 'ขออภัย เกิดข้อผิดพลาดในการส่งข้อความ กรุณาลองใหม่อีกครั้ง')
      return errorMsg
    }
  }

  // Legacy method for compatibility (now uses AI)
  const sendAgentResponse = async (userMessage, delay = 0) => {
    // Add small delay for UX
    if (delay > 0) {
      await new Promise(resolve => setTimeout(resolve, delay))
    }
    
    return await sendUserMessage(userMessage)
  }

  // Mark messages as read
  const markAsRead = () => {
    chatState.value.unreadCount = 0
  }

  // Clear chat history (local only, keeps AI history)
  const clearChat = () => {
    chatState.value.messages = []
    chatState.value.unreadCount = 0
  }

  // Clear all chat data including AI session
  const clearAllChatData = async () => {
    chatState.value.messages = []
    chatState.value.unreadCount = 0
    chatState.value.sessionId = null
    chatState.value.isConnected = false
    
    // Clear AI session
    clearAISession()
  }

  // Reload conversation from AI with smooth updates
  const reloadConversation = async () => {
    // Prevent multiple simultaneous reloads
    if (chatState.value.isReloading) {
      console.log('[LiveChat] Reload already in progress, skipping')
      return false
    }

    chatState.value.isReloading = true
    chatState.value.lastReloadTime = Date.now() // Update last reload time
    
    try {
      console.log('[LiveChat] Starting smooth conversation reload...')
      const history = await loadConversationHistory()
      
      if (history && history.length > 0) {
        const formattedMessages = formatHistoryToMessages(history)
        
        // Smart update: compare and apply changes smoothly
        const currentMessages = chatState.value.messages
        const newMessages = formattedMessages
        
        console.log(`[LiveChat] Current: ${currentMessages.length}, New: ${newMessages.length} messages`)
        
        // If no current messages, load all at once (initial load)
        if (currentMessages.length === 0) {
          chatState.value.messages = newMessages
          console.log('[LiveChat] Initial load: All messages loaded')
          return true
        }
        
        // Check for new messages at the end (most common case)
        if (newMessages.length > currentMessages.length) {
          const newCount = newMessages.length - currentMessages.length
          console.log(`[LiveChat] ${newCount} new message(s) detected`)
          
          // Verify existing messages are the same (prevent full reload if possible)
          let existingMessagesMatch = true
          for (let i = 0; i < currentMessages.length; i++) {
            const current = currentMessages[i]
            const newMsg = newMessages[i]
            
            if (!newMsg || current.text !== newMsg.text || current.type !== newMsg.type) {
              existingMessagesMatch = false
              break
            }
          }
          
          if (existingMessagesMatch) {
            // Only append new messages (smooth)
            const messagesToAdd = newMessages.slice(currentMessages.length)
            console.log('[LiveChat] Appending new messages smoothly:', messagesToAdd.length)
            
            // Set smooth loading state
            chatState.value.isLoadingSmoothly = true
            chatState.value.pendingMessages = messagesToAdd.length
            
            // Add new messages one by one with small delay for smooth effect
            for (let i = 0; i < messagesToAdd.length; i++) {
              setTimeout(() => {
                chatState.value.messages.push(messagesToAdd[i])
                chatState.value.pendingMessages--
                
                // Clear smooth loading when done
                if (chatState.value.pendingMessages === 0) {
                  chatState.value.isLoadingSmoothly = false
                }
              }, i * 150) // 150ms delay between each message for smoother effect
            }
            
            return true
          }
        }
        
        // Check if only the last message changed (AI response update)
        if (newMessages.length === currentMessages.length) {
          const lastCurrentMsg = currentMessages[currentMessages.length - 1]
          const lastNewMsg = newMessages[newMessages.length - 1]
          
          if (lastCurrentMsg && lastNewMsg && 
              lastCurrentMsg.type === lastNewMsg.type &&
              lastCurrentMsg.text !== lastNewMsg.text) {
            console.log('[LiveChat] Last message updated, applying smooth change')
            
            // Smooth update of last message
            chatState.value.messages[chatState.value.messages.length - 1] = lastNewMsg
            return true
          }
        }
        
        // Fallback: Full update (less smooth but necessary for major changes)
        console.log('[LiveChat] Major changes detected, applying full update')
        chatState.value.messages = newMessages
      }
      
      return true
    } catch (error) {
      console.error('Error reloading conversation:', error)
      return false
    } finally {
      chatState.value.isReloading = false
    }
  }

  // Get chat statistics
// Stats and monitoring
const chatStats = computed(() => ({
  sessionId: sessionId.value,
  isAIMode: isAIMode.value,
  isLoadingResponse: isLoadingResponse.value,
  totalMessages: messages.value.length,
  historyMessages: messages.value.filter(m => m.isHistory).length,
  newMessages: messages.value.filter(m => !m.isHistory).length,
  lastResponseMetadata: lastResponseMetadata.value
}))  // Export chat data (for analytics or persistence)
  const exportChatData = () => {
    return {
      sessionId: chatState.value.sessionId,
      messages: chatState.value.messages,
      stats: chatStats.value,
      aiSessionId: aiSessionId.value,
      conversationHistory: conversationHistory.value,
      exportedAt: new Date().toISOString()
    }
  }

  // Disconnect chat
  const disconnectChat = () => {
    chatState.value.isConnected = false
    chatState.value.agentStatus = 'Offline'
    
    // Disconnect WebSocket
    wsDisconnect()
  }

  // Reconnect chat
  const reconnectChat = async () => {
    chatState.value.isConnected = true
    chatState.value.agentStatus = 'Online'
    
    // Reconnect WebSocket
    try {
      await wsConnect()
    } catch (error) {
      console.error('[LiveChat] Failed to reconnect WebSocket:', error)
    }
    
    // Try to reload conversation
    await reloadConversation()
  }

  // WebSocket utility functions
  const getWebSocketStats = () => {
    return wsGetConnectionStats()
  }

  const clearWebSocketNotifications = () => {
    chatState.value.wsNotifications = []
    wsClearNotifications()
  }

  // Smooth loading state helpers
  const getSmoothLoadingState = () => {
    return {
      isLoadingSmoothly: chatState.value.isLoadingSmoothly,
      pendingMessages: chatState.value.pendingMessages,
      isReloading: chatState.value.isReloading
    }
  }

  const isChatUpdating = computed(() => {
    return chatState.value.isReloading || chatState.value.isLoadingSmoothly
  })

  return {
    // State
    chatState: computed(() => chatState.value),
    isConnected: computed(() => chatState.value.isConnected),
    agentStatus: computed(() => chatState.value.agentStatus),
    messages: computed(() => chatState.value.messages),
    unreadCount: computed(() => chatState.value.unreadCount),
    sessionId: computed(() => chatState.value.sessionId),
    isLoadingResponse: computed(() => isLoadingResponse.value),
    personalInfo: computed(() => personalInfo.value), // เพิ่มข้อมูลส่วนบุคคล
    
    // Chat Panel State
    isChatPanelOpen: computed(() => isChatPanelOpen.value),
    
    // WebSocket State
    wsConnected: computed(() => chatState.value.wsConnected),
    wsNotifications: computed(() => chatState.value.wsNotifications),
    wsIsConnecting: computed(() => wsIsConnecting.value),
    wsConnectionId: computed(() => wsConnectionId.value),
    
    // Smooth Loading State
    isChatUpdating,
    getSmoothLoadingState,
    
    // Methods
    initializeChat,
    initializeWebSocket,
    openChatPanel,
    closeChatPanel,
    addMessage,
    removeMessage,
    sendUserMessage,
    sendAgentResponse, // Legacy compatibility
    markAsRead,
    clearChat,
    clearAllChatData,
    reloadConversation,
    disconnectChat,
    reconnectChat,
    getPersonalInfoStatus, // เพิ่มฟังก์ชันตรวจสอบสถานะข้อมูล
    
    // WebSocket Methods
    getWebSocketStats,
    clearWebSocketNotifications,
    
    // Computed
    chatStats,
    exportChatData
  }
}

// Utility functions for chat
export const useLiveChatUtils = () => {
  // Format message time
  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    })
  }

  // Format relative time
  const formatRelativeTime = (timestamp) => {
    const now = new Date()
    const diff = now - timestamp
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    return `${days}d ago`
  }

  // Validate message
  const validateMessage = (message, maxLength = 500) => {
    if (!message || typeof message !== 'string') {
      return { isValid: false, error: 'Message must be a string' }
    }
    
    if (message.trim().length === 0) {
      return { isValid: false, error: 'Message cannot be empty' }
    }
    
    if (message.length > maxLength) {
      return { isValid: false, error: `Message too long (max ${maxLength} characters)` }
    }
    
    return { isValid: true }
  }

  // Generate unique ID
  const generateId = (prefix = 'id') => {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  // Sanitize HTML
  const sanitizeHtml = (html) => {
    const div = document.createElement('div')
    div.textContent = html
    return div.innerHTML
  }

  return {
    formatTime,
    formatRelativeTime,
    validateMessage,
    generateId,
    sanitizeHtml
  }
}
